import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { mh, STAGES, AUTONOMY } from "@/lib/mh";
import { Eyebrow } from "@/components/mh/bits";
import { AgentRow } from "@/components/mh/AgentRow";
import { useDeptMap } from "@/components/mh/hooks";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Filter = ({ value, onChange, options, placeholder, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 rounded-full bg-white/[0.03] border-white/10 text-sm" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-[#0A0A0F] border-white/10">
      <SelectItem value="all">{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function Atlas() {
  const deps = useDeptMap();
  const [f, setF] = useState({ department: "all", stage: "all", autonomy: "all", status: "all" });
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "all"));
    if (search) params.search = search;
    const t = setTimeout(() => mh.agents(params).then((r) => setAgents(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [f, search]);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="atlas-page">
      <header className="mh-rise">
        <Eyebrow>The atlas</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">Every operator, <em className="text-muted-foreground">one index.</em></h1>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        <div className="relative lg:col-span-1 sm:col-span-2">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search agents…" data-testid="atlas-search"
            className="w-full h-10 pl-10 pr-4 rounded-full bg-white/[0.03] border border-white/10 text-sm outline-none focus:border-white/30 transition-colors" />
        </div>
        <Filter testId="filter-department" value={f.department} onChange={set("department")} placeholder="All departments" options={Object.values(deps).map((d) => ({ value: d.slug, label: d.name }))} />
        <Filter testId="filter-stage" value={f.stage} onChange={set("stage")} placeholder="All stages" options={STAGES.map((s) => ({ value: s, label: s }))} />
        <Filter testId="filter-autonomy" value={f.autonomy} onChange={set("autonomy")} placeholder="All autonomy" options={AUTONOMY.map((s) => ({ value: s, label: s }))} />
        <Filter testId="filter-status" value={f.status} onChange={set("status")} placeholder="All statuses" options={[{ value: "live", label: "Live" }, { value: "in_dev", label: "In dev" }, { value: "not_started", label: "Not started" }]} />
      </div>
      <div className="font-mono text-xs text-muted-foreground" data-testid="atlas-count">{agents.length} agents</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
        {agents.map((a, i) => <AgentRow key={a.slug} a={a} dept={deps[a.department]} i={i} />)}
      </div>
    </div>
  );
}
